import ViewDayIcon from "@mui/icons-material/CalendarViewDay";
import ViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import UploadFileIcon from "@mui/icons-material/CloudUploadOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import SaveIcon from "@mui/icons-material/Save";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ViewTimelineOutlinedIcon from "@mui/icons-material/ViewTimelineOutlined";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Snackbar,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { addDays, endOfWeek, format, isSameDay, startOfDay, startOfWeek, subDays } from "date-fns";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSelector } from "react-redux";

import { LaborStandard, laborStandardsApi } from "../../../services/labor-standards-api";
import { productionPlanApi } from "../../../services/production-plan-api";
import { Product, productsApi } from "../../../services/products-api";
import {
	parseAttributeValues,
	WorkOrderAttributeDefinition, // eslint-disable-line sort-imports
	workOrderAttributesApi,
} from "../../../services/work-order-attributes-api";
// Read-only reuse of the labor-requirements preview so the Work Plan can show an
// order's work content without a second copy of the operations/phases UI.
import LaborStandardPreviewDialog from "../labor-requirements/LaborStandardPreviewDialog";
import {
	EmptyState,
	GhostButton,
	MonoText,
	planner,
	PlannerHeader, // eslint-disable-line sort-imports
	PlannerPage,
	PrimaryButton,
	SectionLabel,
	StaffingChip,
} from "../planner-ui";
import { PlanReportRow, planReportRows } from "../utils/reports";
import { findStandardForOrder } from "../utils/standard-matching";
import { toLocalIso } from "../utils/time";
import { downloadSheet, ReportColumn } from "../utils/xlsx-report"; // eslint-disable-line sort-imports
import { DraggableCard, DropCell } from "./board/drop-cell";
import { completeUploadedOrders } from "./board/plan-edits";
import ProductPickerPopover, { PickerTarget } from "./board/product-picker-popover";
import ProductShelf from "./board/product-shelf";
import { usePlanEdits } from "./board/use-plan-edits";
import AIUploadDialog from "./components/AIUploadDialog";
import CampaignDialog from "./components/CampaignDialog";
import CopyMOForm from "./components/CopyMOForm";
import CopyWeekPreviewDialog from "./components/CopyWeekPreviewDialog";
import JobOrderIngestDialog from "./components/JobOrderIngestDialog";
import MOForm from "./components/MOForm";
import ProductionPlanDayView from "./components/ProductionPlanDayView";
import UploadProductionPlanDialog from "./components/UploadProductionPlanDialog";
import {
	CreateWorkOrderDto,
	isNonProductionOrder,
	ManufacturingOrder, // eslint-disable-line sort-imports
	NonProductionOrderType,
} from "./utils/calculations";
import { DAYS_OF_WEEK } from "./utils/constants";
import { describeOrder, LineShift, LineShiftsContext, shiftKey, shiftsByLine } from "./utils/line-shifts"; // eslint-disable-line sort-imports
import { PlantLine } from "./utils/schedule-upload";

const PLAN_COLUMNS: ReportColumn<PlanReportRow>[] = [
	{ header: "Date", key: "date", width: 12 },
	{ header: "Work center", key: "workcenter", width: 24 },
	{ header: "Product", key: "product", width: 40 },
	{ header: "Batch", key: "batch", width: 10 },
	{ header: "Type", key: "type", width: 12 },
	{ header: "Start", key: "start", width: 17 },
	{ header: "End", key: "end", width: 17 },
	{ header: "Hours", key: "hours", width: 8 },
	{ header: "Labor standard", key: "standard", width: 40 },
	{ header: "Job order #", key: "jobOrder", width: 12 },
];

const WC_COLUMN_WIDTH = 172;
// Today reads as one warm alloy band down the board; the brick caps label and
// date in the header do the actual signalling (brick is scarce by rule).
const TODAY_TINT = planner.canvas;
const TODAY_HEADER_BG = planner.canvas;
// Columns narrower than this collapse cards to the dense single-line variant.
const DENSE_COLUMN_WIDTH = 120;
// Minimum day-column width in the 2W/4W horizons (board scrolls horizontally).
const MIN_MULTIWEEK_COLUMN_WIDTH = 90;

type ViewMode = "day" | "week" | "2w" | "4w";

const VIEW_SPAN_DAYS: Record<ViewMode, number> = {
	day: 7,
	week: 7,
	"2w": 14,
	"4w": 28,
};

// Tint pairs for non-production blocks (cleaning / changeover / setup /
// maintenance / inspection). bg is the card fill, fg the dash + label ink.
// Every pair is a Wilya token: the status ramp where the block carries a status
// meaning, and the weld support color for cleaning (which is neither good nor
// bad). Cleaning's wash is derived from the weld token at 10% rather than a new
// literal — tokens.ts is the source of truth and has no weldSoft.
const BLOCK_STYLES: Record<NonProductionOrderType, { bg: string; fg: string; label: string }> = {
	cleaning: { bg: `${planner.weld}1A`, fg: planner.weld, label: "Cleaning" },
	changeover: { bg: planner.partialSoft, fg: planner.partial, label: "Changeover" },
	setup: { bg: planner.unplannedSoft, fg: planner.unplanned, label: "Setup" },
	maintenance: { bg: planner.unstaffedSoft, fg: planner.unstaffed, label: "Maintenance" },
	inspection: { bg: planner.unplannedSoft, fg: planner.unplanned, label: "Inspection" },
};

// Two lines then ellipsis — keeps a long product name from making a week card
// ten lines tall. The full text is always available in the card's tooltip.
const CLAMP_TWO_LINES = {
	display: "-webkit-box",
	WebkitLineClamp: 2,
	WebkitBoxOrient: "vertical",
	overflow: "hidden",
} as const;

// Neutral pill on the card's chip row (time range, unit number).
const neutralPillSx = {
	...planner.type.metaStrong,
	px: 1,
	py: 0.25,
	borderRadius: `${planner.radiusSm}px`,
	bgcolor: planner.surfaceSubtle,
	border: `1px solid ${planner.hairline}`,
	color: planner.inkSecondary,
	whiteSpace: "nowrap",
} as const;

// Parse the sku field into a bold headline + monospaced detail line.
// Vehicle-world skus look like "JO 4082394 / Unit 26EL1025 / VIN 3FT...":
// the first segment is the headline, the rest render as MonoText.
const parseMOTitle = (mo: ManufacturingOrder): { headline: string; detail: string | null } => {
	const raw = (mo.sku || "").trim();
	if (/\s\/\s/.test(raw)) {
		const segments = raw.split(/\s\/\s/).map((segment) => segment.trim()).filter(Boolean);
		if (segments.length > 1) {
			return { headline: segments[0], detail: segments.slice(1).join(" · ") };
		}
	}
	return {
		headline: raw || mo.productName || mo.productItemNumber || "Untitled order",
		detail: null,
	};
};

// Resolve the card content for an MO from its configurable attribute values
// (see work-order-attributes-api). Returns nulls/empties when the MO carries
// no attribute values so the caller can fall back to sku parsing.
const resolveAttributeCard = (
	mo: ManufacturingOrder,
	definitions: WorkOrderAttributeDefinition[]
): {
	headline: string | null;
	identifiers: string[];
	chips: { key: string; label: string; value: string }[];
	details: string[];
} => {
	const values = parseAttributeValues(mo.attributeValues);
	const active = definitions
		.filter((definition) => definition.isActive)
		.sort((a, b) => a.sortOrder - b.sortOrder);
	const bySlot = (slot: string) =>
		active
			.filter((definition) => definition.displaySlot === slot)
			.map((definition) => ({ definition, value: (values[definition.attrKey] || "").trim() }))
			.filter((entry) => Boolean(entry.value));
	return {
		headline: bySlot("headline")[0]?.value ?? null,
		identifiers: bySlot("identifier").map((entry) => entry.value),
		chips: bySlot("chip").map((entry) => ({
			key: entry.definition.attrKey,
			label: entry.definition.label,
			value: entry.value,
		})),
		details: bySlot("detail").map((entry) => `${entry.definition.label} ${entry.value}`),
	};
};

// A VIN is 17 characters of noise at week-glance zoom: it never fits, it is not
// how anyone refers to a truck on the floor, and the unit number beside it
// already identifies the vehicle. It stays on the card's tooltip, in the job
// order dialog and on the Day view — this only drops it from the week card.
const VIN_PATTERN = /^(vin\b[:\s]*)?[A-HJ-NPR-Z0-9]{17}$/i;

const withoutVin = (identifiers: string[]): string[] =>
	identifiers.filter((value) => !VIN_PATTERN.test(value.trim()));

// Quiet inline meta value: a bare number reads as nothing on its own, so short
// numeric attribute values keep their label ("Rev 1") while text values stand
// alone ("Rentokil").
const inlineMetaText = (label: string, value: string) =>
	/^[0-9.]+$/.test(value) || value.length <= 3 ? `${label} ${value}` : value;

// Small accent-soft chip identifying an order's position in a campaign
// (sequenced series, e.g. batch 4 of 9). Rendered next to the time pill.
const CampaignChip: React.FC<{ mo: ManufacturingOrder }> = ({ mo }) => {
	if (mo.campaignId == null) return null;
	return (
		<Tooltip title={`Campaign order ${mo.campaignSeq ?? "?"}`}>
			<Box
				component="span"
				sx={{
					...planner.type.metaStrong,
					px: 0.75,
					py: 0.25,
					borderRadius: `${planner.radiusXs}px`,
					bgcolor: planner.accentSoft,
					color: planner.accent,
					fontFamily: planner.fontMono,
					fontVariantNumeric: "tabular-nums",
					whiteSpace: "nowrap",
				}}
			>
				#{mo.campaignSeq ?? "?"}
			</Box>
		</Tooltip>
	);
};

// Hover action cluster shared by every card variant
const MOCardActions: React.FC<{
	onEdit: () => void;
	onCopy: () => void;
	onDelete: () => void;
	onViewOperations?: () => void;
	canViewOperations?: boolean;
	/** "Make something else here": swap the product in place. */
	onSwap?: (anchor: HTMLElement) => void;
}> = ({ onEdit, onCopy, onDelete, onViewOperations, canViewOperations, onSwap }) => (
	<Box
		className="mo-card-actions"
		sx={{
			position: "absolute",
			top: 4,
			right: 4,
			display: "flex",
			gap: 0.25,
			opacity: 0,
			transition: "opacity 120ms ease",
			bgcolor: planner.surface,
			borderRadius: `${planner.radiusSm}px`,
		}}
	>
		{onViewOperations && (
			<Tooltip
				title={
					canViewOperations
						? "View operations"
						: "No labor standard matched — no operations to show"
				}
			>
				<span>
					<IconButton
						size="small"
						disabled={!canViewOperations}
						onClick={(e) => {
							e.stopPropagation();
							onViewOperations();
						}}
						sx={{
							p: 0.5,
							color: planner.inkMuted,
							"&:hover": { color: planner.ink },
							"&.Mui-disabled": { color: planner.hairlineStrong },
						}}
					>
						<ViewTimelineOutlinedIcon sx={{ fontSize: 15 }} />
					</IconButton>
				</span>
			</Tooltip>
		)}
		{onSwap && (
			<Tooltip title="Make something else here">
				<IconButton
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						onSwap(e.currentTarget);
					}}
					sx={{ p: 0.5, color: planner.inkMuted, "&:hover": { color: planner.ink } }}
				>
					<SwapHorizIcon sx={{ fontSize: 15 }} />
				</IconButton>
			</Tooltip>
		)}
		<Tooltip title="Copy">
			<IconButton
				size="small"
				onClick={(e) => {
					e.stopPropagation();
					onCopy();
				}}
				sx={{ p: 0.5, color: planner.inkMuted, "&:hover": { color: planner.ink } }}
			>
				<ContentCopyIcon sx={{ fontSize: 15 }} />
			</IconButton>
		</Tooltip>
		<Tooltip title="Edit">
			<IconButton
				size="small"
				onClick={(e) => {
					e.stopPropagation();
					onEdit();
				}}
				sx={{ p: 0.5, color: planner.inkMuted, "&:hover": { color: planner.ink } }}
			>
				<EditOutlinedIcon sx={{ fontSize: 15 }} />
			</IconButton>
		</Tooltip>
		<Tooltip title="Delete">
			<IconButton
				size="small"
				onClick={(e) => {
					e.stopPropagation();
					onDelete();
				}}
				sx={{ p: 0.5, color: planner.inkMuted, "&:hover": { color: planner.unstaffed } }}
			>
				<DeleteOutlineIcon sx={{ fontSize: 15 }} />
			</IconButton>
		</Tooltip>
	</Box>
);

// A single MO card in a week cell. White card, hairline border, gray left
// accent only when no labor standard is attached. Actions appear on hover.
// Non-production order types (cleaning, changeover, …) render as compact
// tinted blocks; `continuation` renders the slim ghost strip shown on the
// extra day columns a multi-day order touches; `dense` is the single-line
// variant used when day columns are narrower than 120px.
const MOCard: React.FC<{
	mo: ManufacturingOrder;
	attributeDefinitions: WorkOrderAttributeDefinition[];
	onEdit: () => void;
	onCopy: () => void;
	onDelete: () => void;
	onViewOperations: () => void;
	canViewOperations: boolean;
	onSwap?: (anchor: HTMLElement) => void;
	continuation?: boolean;
	dense?: boolean;
}> = ({
	mo,
	attributeDefinitions,
	onEdit,
	onCopy,
	onDelete,
	onViewOperations,
	canViewOperations,
	onSwap,
	continuation,
	dense,
}) => {
	const startTime = new Date(mo.startTime);
	const endTime = mo.endTime ? new Date(mo.endTime) : null;
	const hasStandard = Boolean(mo.laborStandardId);
	const blockStyle = isNonProductionOrder(mo.orderType) ? BLOCK_STYLES[mo.orderType] : null;
	const crossesMidnight = Boolean(endTime && !isSameDay(startTime, endTime));
	// The shift(s) the order runs in on its line, and its overtime (no shift covers it).
	const lineShifts = useContext(LineShiftsContext);
	const shifts = describeOrder(mo.startTime, mo.endTime, lineShifts.get((mo.workstation || "").toLowerCase()));
	const shiftPrefix = shifts.label ? `${shifts.label} · ` : "";
	const overtimeChip = shifts.offShiftMinutes > 0 && (
		<Tooltip title={`Runs ${(shifts.offShiftMinutes / 60).toFixed(1).replace(/\.0$/, "")} h past the line's shifts — finished on overtime`}>
			<Box
				component="span"
				sx={{
					...planner.type.metaStrong,
					px: 0.75,
					borderRadius: `${planner.radiusSm}px`,
					border: `1px solid ${planner.partial}55`,
					bgcolor: planner.partialSoft,
					color: planner.partial,
					whiteSpace: "nowrap",
				}}
			>
				+{(shifts.offShiftMinutes / 60).toFixed(1).replace(/\.0$/, "")} h OT
			</Box>
		</Tooltip>
	);
	const accentColor = blockStyle
		? blockStyle.fg
		: hasStandard
			? planner.accent
			: planner.unplanned;

	// Slim ghost strip on the continuation days of a multi-day order
	if (continuation) {
		return (
			<Box
				onClick={onEdit}
				sx={{
					border: `${planner.borderWidth}px dashed ${planner.hairlineStrong}`,
					borderLeft: `3px solid ${accentColor}`,
					borderRadius: `${planner.radiusSm}px`,
					bgcolor: blockStyle ? blockStyle.bg : planner.surfaceSubtle,
					px: 0.75,
					py: 0.5,
					mb: 0.5,
					cursor: "pointer",
					minWidth: 0,
					"&:hover": { borderColor: planner.inkMuted },
				}}
			>
				<Typography
					sx={{
						...planner.type.meta,
						fontFamily: planner.fontMono,
						color: blockStyle ? blockStyle.fg : planner.inkSecondary,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{endTime ? `cont. — until ${format(endTime, "EEE h:mm a")}` : "cont."}
				</Typography>
			</Box>
		);
	}

	// Non-production block: compact tinted card, type label + time range only
	if (blockStyle) {
		const blockLabel = (mo.sku || "").trim() || blockStyle.label;
		return (
			<Box
				onClick={onEdit}
				sx={{
					position: "relative",
					bgcolor: blockStyle.bg,
					// Hairline in the block's own ink — this is what gives setup /
					// inspection the "draft" look instead of a resting shadow.
					border: `${planner.borderWidth}px solid ${blockStyle.fg}33`,
					borderRadius: `${planner.radiusSm}px`,
					px: 1,
					py: dense ? 0.5 : 0.75,
					mb: dense ? 0.5 : 0.75,
					cursor: "pointer",
					minWidth: 0,
					transition: "border-color 150ms ease",
					"&:hover": { borderColor: blockStyle.fg },
					"&:hover .mo-card-actions": { opacity: 1 },
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
					<Box
						sx={{
							width: 3,
							height: 14,
							borderRadius: `${planner.radiusXs}px`,
							bgcolor: blockStyle.fg,
							flexShrink: 0,
						}}
					/>
					<Typography
						sx={{
							...planner.type.metaStrong,
							color: blockStyle.fg,
							// Wrap rather than truncate: "Type-A clean" losing its last
							// word to an ellipsis tells the planner nothing.
							overflowWrap: "anywhere",
							...CLAMP_TWO_LINES,
							minWidth: 0,
						}}
					>
						{blockLabel}
					</Typography>
				</Box>
				{!dense && (
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							gap: 0.5,
							mt: 0.5,
							pl: 1.5,
							minWidth: 0,
						}}
					>
						{/* Type and time are separate spans so the line can WRAP between
						    them in a narrow column, while the time range itself never
						    breaks mid-value. As one nowrap string this overflowed the
						    card and spilled into the next day. */}
						<Typography
							component="span"
							sx={{
								...planner.type.meta,
								fontFamily: planner.fontMono,
								color: blockStyle.fg,
								opacity: 0.85,
								fontVariantNumeric: "tabular-nums",
								whiteSpace: "nowrap",
							}}
						>
							{blockStyle.label} ·
						</Typography>
						<Typography
							component="span"
							sx={{
								...planner.type.meta,
								fontFamily: planner.fontMono,
								color: blockStyle.fg,
								opacity: 0.85,
								fontVariantNumeric: "tabular-nums",
								whiteSpace: "nowrap",
							}}
						>
							{shiftPrefix}
							{format(startTime, "HH:mm")}
							{endTime && `–${format(endTime, "HH:mm")}`}
						</Typography>
						{overtimeChip}
						<CampaignChip mo={mo} />
						{crossesMidnight && (
							<Typography
								component="span"
								sx={{ ...planner.type.meta, color: blockStyle.fg, opacity: 0.85, whiteSpace: "nowrap" }}
							>
								▸ continues
							</Typography>
						)}
					</Box>
				)}
				<MOCardActions onEdit={onEdit} onCopy={onCopy} onDelete={onDelete} onSwap={onSwap} />
			</Box>
		);
	}
	// Configurable attributes drive the card when the MO carries values and the
	// company's definitions are loaded; otherwise fall back to sku parsing.
	const attributeCard = resolveAttributeCard(mo, attributeDefinitions);
	const hasAttributeContent =
		Boolean(attributeCard.headline) ||
		attributeCard.identifiers.length > 0 ||
		attributeCard.chips.length > 0 ||
		attributeCard.details.length > 0;
	const parsedTitle = parseMOTitle(mo);
	const headline = attributeCard.headline || parsedTitle.headline;
	// The customer's batch / process-order number leads the identifier line
	// when the step carries one — it is how the floor refers to the batch.
	const batchLabel = (mo.batchNumber || "").trim();
	const identifierDetail = hasAttributeContent
		? withoutVin(attributeCard.identifiers).join(" · ")
		: withoutVin((parsedTitle.detail || "").split(" · ")).join(" · ");
	const detail =
		[batchLabel ? `Batch ${batchLabel}` : null, identifierDetail || null]
			.filter(Boolean)
			.join(" · ") || null;
	const showProductLine = Boolean(mo.productName) && mo.productName !== headline;

	// Dense single-line variant for narrow (2W/4W) columns: staffing dash + headline
	if (dense) {
		return (
			<Box
				onClick={onEdit}
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.75,
					bgcolor: planner.surface,
					border: `${planner.borderWidth}px solid ${planner.hairline}`,
					borderRadius: `${planner.radiusSm}px`,
					px: 0.75,
					py: 0.5,
					mb: 0.5,
					cursor: "pointer",
					minWidth: 0,
					transition: "border-color 150ms ease",
					"&:hover": { borderColor: planner.navy },
				}}
			>
				<Box
					sx={{
						width: 3,
						height: 12,
						borderRadius: `${planner.radiusXs}px`,
						bgcolor: accentColor,
						flexShrink: 0,
					}}
				/>
				<Typography
					sx={{
						...planner.type.metaStrong,
						color: planner.ink,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
						minWidth: 0,
					}}
				>
					{headline}
				</Typography>
			</Box>
		);
	}

	// The card has to fit its day column, not widen the 7-day grid: the first
	// chip (a short unit number) shares the time row, the labor standard keeps a
	// row of its own, and everything else drops to one quiet meta line.
	const [leadChip, ...trailingChips] = attributeCard.chips;
	// The standard is usually named after the product, already on the card: then the chip just says it is there.
	const squash = (text?: string | null) => (text ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
	const standardRepeatsProduct = Boolean(
		mo.laborStandardName && mo.productName &&
		(squash(mo.laborStandardName).includes(squash(mo.productName)) || squash(mo.productName).includes(squash(mo.laborStandardName)))
	);
	const standardLabel = !mo.laborStandardName ? "Standard" : standardRepeatsProduct ? "Standard ✓" : mo.laborStandardName;
	const metaLine = [
		...trailingChips.map((chip) => inlineMetaText(chip.label, chip.value)),
		...attributeCard.details,
	].join(" · ");

	return (
		<Box
			onClick={onEdit}
			sx={{
				position: "relative",
				bgcolor: planner.surface,
				border: `${planner.borderWidth}px solid ${planner.hairline}`,
				borderLeft: hasStandard
					? `${planner.borderWidth}px solid ${planner.hairline}`
					: `3px solid ${planner.unplanned}`,
				borderRadius: `${planner.radiusSm}px`,
				boxShadow: planner.shadowRest,
				p: 1.25,
				mb: 0.75,
				cursor: "pointer",
				minWidth: 0,
				overflow: "hidden",
				transition: "border-color 150ms ease",
				"&:hover": { borderColor: planner.navy },
				"&:hover .mo-card-actions": { opacity: 1 },
			}}
		>
			<Tooltip title={headline}>
				<Typography
					sx={{
						...planner.type.bodyStrong,
						color: planner.ink,
						lineHeight: 1.3,
						wordBreak: "break-word",
						...CLAMP_TWO_LINES,
						pr: 4,
					}}
				>
					{headline}
				</Typography>
			</Tooltip>
			{detail && (
				// A VIN or job-order number is one token: it truncates, it never
				// breaks mid-string. The full value lives in the tooltip.
				<Tooltip title={detail}>
					<Box sx={{ minWidth: 0, mt: 0.25 }}>
						<MonoText
							sx={{
								display: "block",
								lineHeight: 1.4,
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{detail}
						</MonoText>
					</Box>
				</Tooltip>
			)}
			{showProductLine && (
				<Tooltip title={mo.productName ?? ""}>
					<Typography
						sx={{
							...planner.type.meta,
							color: planner.inkSecondary,
							mt: 0.5,
							lineHeight: 1.35,
							...CLAMP_TWO_LINES,
						}}
					>
						{mo.productName}
					</Typography>
				</Tooltip>
			)}
			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.75, minWidth: 0 }}>
				<Box
					component="span"
					sx={{
						...neutralPillSx,
						fontFamily: planner.fontMono,
						fontVariantNumeric: "tabular-nums",
						flexShrink: 0,
					}}
				>
					{shiftPrefix}
					{format(startTime, "HH:mm")}
					{endTime && `–${format(endTime, "HH:mm")}`}
				</Box>
				{overtimeChip}
				{leadChip && (
					<Tooltip title={`${leadChip.label} ${leadChip.value}`}>
						<Box
							component="span"
							sx={{ ...neutralPillSx, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
						>
							{leadChip.value}
						</Box>
					</Tooltip>
				)}
				<CampaignChip mo={mo} />
				{crossesMidnight && (
					<Typography
						component="span"
						sx={{ ...planner.type.meta, color: planner.inkMuted, whiteSpace: "nowrap" }}
					>
						▸ continues
					</Typography>
				)}
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, minWidth: 0 }}>
				{hasStandard ? (
					<Tooltip title={mo.laborStandardName || "Labor standard attached"}>
						<Box
							component="span"
							sx={{
								...planner.type.metaStrong,
								px: 1,
								py: 0.25,
								borderRadius: `${planner.radiusSm}px`,
								bgcolor: planner.accentSoft,
								color: planner.accent,
								// Standard names carry the sku, work centre and batch size
								// ("238660 - Upfit Bay 1 (1 units)") and never fit a day
								// column. Wrap instead of ellipsing — "238660 - Upfit …"
								// tells the planner nothing.
								overflowWrap: "anywhere",
								...CLAMP_TWO_LINES,
								minWidth: 0,
							}}
						>
							{standardLabel}
						</Box>
					</Tooltip>
				) : (
					<StaffingChip state="unplanned" label="No standard" />
				)}
				{mo.quantity > 1 && (
					<Box
						component="span"
						sx={{
							...planner.type.metaStrong,
							color: planner.inkMuted,
							fontFamily: planner.fontMono,
							fontVariantNumeric: "tabular-nums",
							whiteSpace: "nowrap",
							flexShrink: 0,
						}}
					>
						×{mo.quantity.toLocaleString()}
					</Box>
				)}
			</Box>
			{metaLine && (
				<Tooltip title={metaLine}>
					<Typography
						sx={{
							...planner.type.meta,
							color: planner.inkMuted,
							mt: 0.5,
							lineHeight: 1.35,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{metaLine}
					</Typography>
				</Tooltip>
			)}
			<MOCardActions
				onEdit={onEdit}
				onCopy={onCopy}
				onDelete={onDelete}
				onViewOperations={onViewOperations}
				canViewOperations={canViewOperations}
				onSwap={onSwap}
			/>
		</Box>
	);
};

const ProductionPlan: React.FC = () => {
	// === DEBUG: Dump entire relevant Redux state ===
	const fullTimeShiftsState = useSelector((state: any) => state.fullTimeShifts);
	const filterStatus = fullTimeShiftsState?.getFullTimeShiftsFilters?.status;
	const filterError = fullTimeShiftsState?.getFullTimeShiftsFilters?.errorMessage;

	useEffect(() => {
		console.log("=== [WorkPlan DEBUG] Redux state.fullTimeShifts keys:", Object.keys(fullTimeShiftsState || {}));
		console.log("=== [WorkPlan DEBUG] filterData:", JSON.stringify(fullTimeShiftsState?.filterData, null, 2));
		console.log("=== [WorkPlan DEBUG] filterData.allWorkcenters:", fullTimeShiftsState?.filterData?.allWorkcenters);
		console.log("=== [WorkPlan DEBUG] getFullTimeShiftsFilters status:", filterStatus);
		console.log("=== [WorkPlan DEBUG] getFullTimeShiftsFilters error:", filterError);
	}, [fullTimeShiftsState, filterStatus, filterError]);

	// Read workcenters from the Redux store (already loaded by SchedulerTabs parent)
	const allWorkcentersFromStore = useSelector(
		(state: any) => state.fullTimeShifts?.filterData?.allWorkcenters ?? []
	);
	// The plan is one plant at a time: only the selected location's work
	// centers make rows. Without this a multi-site company showed every site's
	// centers (and the retired duplicates) under one plant.
	const selectedLocationId = useSelector((state: any) => state.fullTimeShifts?.selectedLocation);
	const workcenters = useMemo(() => {
		const scoped = selectedLocationId
			? allWorkcentersFromStore.filter((wc: any) => wc.locationId === selectedLocationId)
			: allWorkcentersFromStore;
		const source = scoped.length ? scoped : allWorkcentersFromStore;
		return Array.from(new Set(source.map((wc: any) => wc.name as string))).sort();
	}, [allWorkcentersFromStore, selectedLocationId]);
	// Every planned plant's lines, so one upload can carry all plants' plans.
	const allLocationsFromStore = useSelector((state: any) => state.fullTimeShifts?.filterData?.allLocations ?? []);
	const enabledLocationIds = useSelector((state: any) => state.user?.userData?.ftSchedulerEnabledLocations ?? []);
	const plantLines = useMemo<PlantLine[]>(() => {
		const names = new Map<number, string>(allLocationsFromStore.map((location: any) => [location.id, location.name]));
		const seen = new Set<string>();
		return allWorkcentersFromStore
			.filter((wc: any) => enabledLocationIds.includes(wc.locationId) && names.has(wc.locationId))
			.map((wc: any) => ({ name: wc.name as string, locationId: wc.locationId as number, locationName: names.get(wc.locationId) as string }))
			.filter((line: PlantLine) => {
				const key = `${line.locationId}|${line.name}`;
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			});
	}, [allWorkcentersFromStore, allLocationsFromStore, enabledLocationIds]);
	// Treat "idle" and "pending" as loading - data hasn't arrived yet
	const loadingWorkcenters = filterStatus === "pending" || filterStatus === "idle";

	const [productionPlan, setProductionPlan] = useState<ManufacturingOrder[]>([]);
	// When each line works (its own crew's shifts): cards name their shift and show overtime.
	const [lineShifts, setLineShifts] = useState<LineShift[]>([]);
	const lineShiftsByLine = useMemo(() => shiftsByLine(lineShifts), [lineShifts]);
	const [weekStartDate, setWeekStartDate] = useState(
		format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd")
	);
	const [viewMode, setViewMode] = useState<ViewMode>("week");
	const [selectedDayDate, setSelectedDayDate] = useState<Date>(new Date());
	const [showForm, setShowForm] = useState(false);
	const [showCopyForm, setShowCopyForm] = useState(false);
	const [showUploadDialog, setShowUploadDialog] = useState(false);
	const [showAIUploadDialog, setShowAIUploadDialog] = useState(false);
	const [showJobOrderIngestDialog, setShowJobOrderIngestDialog] = useState(false);
	const [showCampaignDialog, setShowCampaignDialog] = useState(false);
	const [copyingMO, setCopyingMO] = useState<ManufacturingOrder | null>(null);
	const [uploadMenuAnchor, setUploadMenuAnchor] = useState<null | HTMLElement>(null);
	const [editingMO, setEditingMO] = useState<ManufacturingOrder | null>(null);
	const [selectedWorkcenter, setSelectedWorkcenter] = useState<string | null>(null);
	const [selectedDate, setSelectedDate] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity?: "success" | "error" | "warning"; undo?: () => void }>({
		open: false,
		message: "",
	});
	const [products, setProducts] = useState<Product[]>([]);
	const [loadingProducts, setLoadingProducts] = useState(false);
	const [showCopyWeekDialog, setShowCopyWeekDialog] = useState(false);
	const companyId = useSelector((state: any) => state?.user?.userData?.companyId);
	const [attributeDefinitions, setAttributeDefinitions] = useState<WorkOrderAttributeDefinition[]>([]);
	// Labor standards back the "View operations" action on a card: the planner can
	// see what an order's hours are made of without leaving the Work Plan.
	const [laborStandards, setLaborStandards] = useState<LaborStandard[]>([]);
	// Uploaded rows get the plant, the line's labor standard and its run time, like a drop.
	const prepareUploadedOrders = useCallback(
		<T extends CreateWorkOrderDto>(orders: T[]): T[] => completeUploadedOrders(orders, laborStandards, selectedLocationId),
		[laborStandards, selectedLocationId]
	);
	const [previewStandard, setPreviewStandard] = useState<LaborStandard | null>(null);
	// Board edits: drag a product onto a cell, drag a card to another cell,
	// swap a card's product in place. Each saves at once and offers Undo.
	const notify = useCallback(
		(message: string, severity: "success" | "error" | "warning" = "success", undo?: () => void) =>
			setSnackbar({ open: true, message, severity, undo }),
		[]
	);
	const edits = usePlanEdits({
		orders: productionPlan,
		setOrders: setProductionPlan,
		standards: laborStandards,
		locationId: selectedLocationId ?? null,
		notify,
	});
	// The product picker serves both "+ Add job order" (pick what runs here)
	// and a card's swap action (make something else here).
	const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
	const [pickerTarget, setPickerTarget] = useState<PickerTarget | null>(null);
	const openSwap = useCallback((mo: ManufacturingOrder, anchor: HTMLElement) => {
		setPickerTarget({ mode: "swap", order: mo });
		setPickerAnchor(anchor);
	}, []);
	const openAddPicker = useCallback((workstation: string, day: Date, anchor: HTMLElement) => {
		setPickerTarget({ mode: "add", workstation, day });
		setPickerAnchor(anchor);
	}, []);
	const closePicker = useCallback(() => {
		setPickerAnchor(null);
		setPickerTarget(null);
	}, []);

	// Horizon span derived from the DAY / WEEK / 2W / 4W toggle
	const spanDays = VIEW_SPAN_DAYS[viewMode];
	const isMultiWeek = viewMode === "2w" || viewMode === "4w";
	const isGridMode = viewMode !== "day";

	// Measure the board card so cards can collapse to the dense single-line
	// variant when day columns get narrower than DENSE_COLUMN_WIDTH.
	const [boardEl, setBoardEl] = useState<HTMLDivElement | null>(null);
	const [boardWidth, setBoardWidth] = useState(0);
	useEffect(() => {
		if (!boardEl) return;
		const observer = new ResizeObserver((entries) => {
			const width = entries[0]?.contentRect?.width;
			if (typeof width === "number") setBoardWidth(width);
		});
		observer.observe(boardEl);
		return () => observer.disconnect();
	}, [boardEl]);
	const columnWidth = useMemo(() => {
		if (!boardWidth) return null;
		const raw = (boardWidth - WC_COLUMN_WIDTH) / spanDays;
		return isMultiWeek ? Math.max(MIN_MULTIWEEK_COLUMN_WIDTH, raw) : raw;
	}, [boardWidth, spanDays, isMultiWeek]);
	const denseCards = columnWidth !== null && columnWidth < DENSE_COLUMN_WIDTH;

	// Fetch the company's configurable work-order attribute definitions once;
	// MO cards render attribute values by display slot when these are loaded.
	useEffect(() => {
		if (!companyId) return;
		workOrderAttributesApi.getByCompany(companyId)
			.then((definitions) => setAttributeDefinitions(definitions))
			.catch((error) => {
				console.error("Failed to fetch work order attribute definitions:", error);
				setAttributeDefinitions([]);
			});
	}, [companyId]);

	// Fetch labor standards once so a card can tell whether it has work content
	// to show, and open it without a round trip when the tree is already loaded.
	useEffect(() => {
		laborStandardsApi.getAll()
			.then((standards) => setLaborStandards(standards))
			.catch((error) => {
				console.error("Failed to fetch labor standards:", error);
				setLaborStandards([]);
			});
	}, []);

	// Fetch products from API
	useEffect(() => {
		const fetchProducts = async () => {
			try {
				setLoadingProducts(true);
				console.log("=== [WorkPlan DEBUG] Fetching products from:", `${process.env.REACT_APP_API_END_POINT}/Products`);
				const data = await productsApi.getAll(false);
				console.log("=== [WorkPlan DEBUG] Products loaded successfully, count:", data?.length);
				setProducts(data);
			} catch (error: any) {
				console.error("=== [WorkPlan DEBUG] Failed to fetch products:", {
					message: error?.message,
					status: error?.response?.status,
					statusText: error?.response?.statusText,
					responseData: error?.response?.data,
					url: error?.config?.url,
				});
				setSnackbar({
					open: true,
					message: `Failed to load products: ${error?.response?.status || "Network error"} - ${error?.message || "Unknown error"}`,
					severity: "error",
				});
			} finally {
				setLoadingProducts(false);
			}
		};
		fetchProducts();
	}, []);

	// Helper to parse date string without timezone issues
	const parseDateString = (dateStr: string): Date => {
		const [year, month, day] = dateStr.split("-").map(Number);
		return new Date(year, month - 1, day);
	};

	// Calculate the visible dates for the current horizon (7 / 14 / 28 days)
	const weekDates = useMemo(() => {
		if (!weekStartDate) return [];
		const weekStart = startOfWeek(parseDateString(weekStartDate), { weekStartsOn: 1 });
		return Array.from({ length: spanDays }, (_, index) => {
			const date = addDays(weekStart, index);
			return {
				day: isMultiWeek ? format(date, "EEE") : DAYS_OF_WEEK[index % 7],
				date,
				dateString: format(date, "yyyy-MM-dd"),
			};
		});
	}, [weekStartDate, spanDays, isMultiWeek]);

	useEffect(() => {
		if (!selectedLocationId) return;
		let cancelled = false;
		const [year, month, day] = weekStartDate.split("-").map(Number);
		const from = new Date(year, month - 1, day);
		const to = addDays(from, spanDays + 1);
		productionPlanApi
			.getLineShifts(selectedLocationId, format(from, "yyyy-MM-dd"), format(to, "yyyy-MM-dd"))
			.then((rows) => {
				if (!cancelled) setLineShifts(rows);
			})
			.catch(() => {
				if (!cancelled) setLineShifts([]);
			});
		return () => {
			cancelled = true;
		};
	}, [selectedLocationId, weekStartDate, spanDays]);

	const loadManufacturingOrders = useCallback(async () => {
		if (!weekStartDate) return;

		setLoading(true);
		try {
			const [year, month, day] = weekStartDate.split("-").map(Number);
			const weekStart = startOfWeek(new Date(year, month - 1, day), { weekStartsOn: 1 });
			// End of the last visible week: 7 days for DAY/WEEK, 14 for 2W, 28 for 4W
			const weekEnd = endOfWeek(addDays(weekStart, spanDays - 7), { weekStartsOn: 1 });
			const apiUrl = `${process.env.REACT_APP_API_END_POINT}/ProductionPlan/range`;
			console.log("=== [WorkPlan DEBUG] Loading MOs - URL:", apiUrl, "params:", {
				startDate: toLocalIso(weekStart),
				endDate: toLocalIso(weekEnd),
			});
			const orders = await productionPlanApi.getByDateRange(
				toLocalIso(weekStart),
				toLocalIso(weekEnd)
			);
			console.log("=== [WorkPlan DEBUG] MOs loaded successfully, count:", orders?.length);
			setProductionPlan(orders);
		} catch (error: any) {
			console.error("=== [WorkPlan DEBUG] Error loading manufacturing orders:", {
				message: error?.message,
				status: error?.response?.status,
				statusText: error?.response?.statusText,
				responseData: error?.response?.data,
				url: error?.config?.url,
				fullError: error,
			});
			setSnackbar({
				open: true,
				message: `Failed to load job orders: ${error?.response?.status || "Network error"} - ${error?.response?.data?.message || error?.message || "Unknown error"}`,
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	}, [weekStartDate, spanDays]);

	// Load manufacturing orders for the current week
	useEffect(() => {
		loadManufacturingOrders();
	}, [loadManufacturingOrders]);

	const handleAdd = (workcenter: string, date: string) => {
		setEditingMO(null);
		setSelectedWorkcenter(workcenter);
		setSelectedDate(date);
		setShowForm(true);
	};

	const handleEdit = (mo: ManufacturingOrder) => {
		setEditingMO(mo);
		setSelectedWorkcenter(mo.workstation);
		const moDate = new Date(mo.startTime);
		setSelectedDate(format(moDate, "yyyy-MM-dd"));
		setShowForm(true);
	};

	const handleDelete = async (id: number) => {
		console.log("Attempting to delete MO with ID:", id);
		try {
			await productionPlanApi.delete(id);
			setProductionPlan(prev => prev.filter(mo => mo.id !== id));
			setSnackbar({
				open: true,
				message: "Job order deleted successfully",
				severity: "success",
			});
		} catch (error: any) {
			console.error("Error deleting manufacturing order:", error);
			const status = error?.response?.status;
			const message = error?.response?.data?.message || error?.message || "Failed to delete job order";

			// If 404, the MO doesn't exist in DB - remove from local state anyway
			if (status === 404) {
				console.log("MO not found in database (404), removing from local state");
				setProductionPlan(prev => prev.filter(mo => mo.id !== id));
				setSnackbar({
					open: true,
					message: "Order removed (was not found in database)",
					severity: "success",
				});
			} else {
				setSnackbar({
					open: true,
					message: `Failed to delete job order (ID: ${id}): ${message}`,
					severity: "error",
				});
			}
		}
	};

	// True when the order resolves to a labor standard, i.e. there is work content
	// (operations and phases) to show for it. The standard it references, or —
	// when it references none — the product's / SKU's standard with a step on
	// the order's work center (the roster and the API resolve it the same way).
	const hasOperations = useCallback(
		(mo: ManufacturingOrder) => Boolean(findStandardForOrder(mo, laborStandards)),
		[laborStandards]
	);

	// Show an order's work content in the labor-requirements preview. The list
	// endpoint may return standards without their operation tree, so the full
	// standard is fetched when the operations are missing.
	const handleViewOperations = useCallback(
		async (mo: ManufacturingOrder) => {
			const matched = findStandardForOrder(mo, laborStandards);
			if (!matched) return;
			if (matched.operations && matched.operations.length > 0) {
				setPreviewStandard(matched);
				return;
			}
			try {
				const full = await laborStandardsApi.getById(matched.id);
				setPreviewStandard(full);
			} catch (error) {
				console.error("Failed to load labor standard operations:", error);
				setSnackbar({
					open: true,
					message: "Could not load the operations for this job order",
					severity: "error",
				});
			}
		},
		[laborStandards]
	);

	const handleCopy = (mo: ManufacturingOrder) => {
		setCopyingMO(mo);
		setShowCopyForm(true);
	};

	const handleSaveCopiedMOs = async (newMOs: Omit<ManufacturingOrder, "id">[]) => {
		try {
			const createdMOs = await productionPlanApi.createBatch(newMOs);
			setProductionPlan(prev => [...prev, ...createdMOs]);
			setShowCopyForm(false);
			setCopyingMO(null);
			setSnackbar({
				open: true,
				message: `${newMOs.length} job order(s) created successfully`,
				severity: "success",
			});
		} catch (error: any) {
			console.error("Error creating manufacturing orders:", error);
			const errorMessage = error?.response?.data?.message || "Failed to create job orders";
			setSnackbar({
				open: true,
				message: errorMessage,
				severity: "error",
			});
		}
	};

	const handleSaveMO = async (moData: Omit<ManufacturingOrder, "id">) => {
		try {
			if (editingMO) {
				await productionPlanApi.update(editingMO.id, moData);
				setProductionPlan(prev =>
					prev.map(mo => (mo.id === editingMO.id ? { ...moData, id: editingMO.id } : mo))
				);
				setSnackbar({
					open: true,
					message: "Job order updated successfully",
					severity: "success",
				});
			} else {
				const createdMO = await productionPlanApi.create(moData);
				setProductionPlan(prev => [...prev, createdMO]);
				setSnackbar({
					open: true,
					message: "Job order created successfully",
					severity: "success",
				});
			}
			setShowForm(false);
			setEditingMO(null);
			setSelectedWorkcenter(null);
			setSelectedDate(null);
		} catch (error: any) {
			console.error("Error saving manufacturing order:", error);
			const errorMessage = error?.response?.data?.message ||
				(editingMO ? "Failed to update job order" : "Failed to create job order");
			setSnackbar({
				open: true,
				message: errorMessage,
				severity: "error",
			});
		}
	};

	const handlePreviousWeek = () => {
		if (loading) return;
		try {
			// Parse the date string explicitly to avoid timezone issues
			const [year, month, day] = weekStartDate.split("-").map(Number);
			const currentDate = new Date(year, month - 1, day);
			const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
			const previousWeekStart = subDays(currentWeekStart, 7);
			const newDate = format(previousWeekStart, "yyyy-MM-dd");
			setWeekStartDate(newDate);
		} catch (error) {
			console.error("Error navigating to previous week:", error);
		}
	};

	const handleNextWeek = () => {
		if (loading) {
			console.log("Navigation blocked: loading is true");
			return;
		}
		try {
			console.log("Navigating to next week. Current weekStartDate:", weekStartDate);
			// Parse the date string explicitly to avoid timezone issues
			const [year, month, day] = weekStartDate.split("-").map(Number);
			const currentDate = new Date(year, month - 1, day);
			const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
			const nextWeekStart = addDays(currentWeekStart, 7);
			const newDate = format(nextWeekStart, "yyyy-MM-dd");
			console.log("Setting new weekStartDate to:", newDate);
			setWeekStartDate(newDate);
		} catch (error) {
			console.error("Error navigating to next week:", error);
		}
	};

	const handleToday = () => {
		if (loading) return;
		try {
			const todayWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
			const newDate = format(todayWeekStart, "yyyy-MM-dd");
			setWeekStartDate(newDate);
		} catch (error) {
			console.error("Error navigating to today:", error);
		}
	};

	// Calculate date range text for display (covers the visible horizon)
	const dateRangeText = useMemo(() => {
		if (!weekStartDate) return "";
		const [year, month, day] = weekStartDate.split("-").map(Number);
		const weekStart = startOfWeek(new Date(year, month - 1, day), { weekStartsOn: 1 });
		const weekEnd = addDays(weekStart, spanDays - 1);
		return `${format(weekStart, "MMMM d")} - ${format(weekEnd, "MMMM d, yyyy")}`;
	}, [weekStartDate, spanDays]);

	// The job orders on screen as a spreadsheet: any week, past ones too.
	const handleDownloadPlan = useCallback(() => {
		void downloadSheet(`work-plan-${weekStartDate || "plan"}.xlsx`, `Work plan ${weekStartDate}`, PLAN_COLUMNS, planReportRows(productionPlan));
	}, [weekStartDate, productionPlan]);

	const handleSaveProductionPlan = async () => {
		if (productionPlan.length === 0) {
			setSnackbar({
				open: true,
				message: "No job orders to save",
				severity: "error",
			});
			return;
		}

		setSaving(true);
		try {
			// Keep the IDs so the backend knows which orders already exist
			const ordersToSave = productionPlan.map((order) => ({
				...order,
				// Include id if it exists (for existing orders that need to be updated)
				id: order.id || undefined,
			}));
			await productionPlanApi.saveProductionPlan(weekStartDate, ordersToSave);
			setSnackbar({
				open: true,
				message: "Production plan saved successfully",
				severity: "success",
			});
		} catch (error: any) {
			console.error("Error saving production plan:", error);
			let errorMessage = error?.response?.data?.message || "Failed to save production plan";

			// If there's detailed conflict information, include it
			if (error?.response?.data?.newOrderTime && error?.response?.data?.conflictingOrderTime) {
				errorMessage = errorMessage + ` Details: Your order (${error.response.data.newOrderTime}) conflicts with existing order (${error.response.data.conflictingOrderTime}).`;
			}

			setSnackbar({
				open: true,
				message: errorMessage,
				severity: "error",
			});
		} finally {
			setSaving(false);
		}
	};

	// Get MO entries for a specific workcenter and date. A multi-day order
	// (endTime past midnight) appears in every day column it touches: the
	// start day carries the full card, later days a continuation strip.
	const getMOsForWorkcenterAndDate = (
		workcenter: string,
		date: Date
	): { mo: ManufacturingOrder; isContinuation: boolean }[] => {
		const dayStart = startOfDay(date);
		return productionPlan
			.filter(mo => mo.workstation === workcenter)
			.flatMap(mo => {
				const moStart = new Date(mo.startTime);
				if (isSameDay(moStart, date)) {
					return [{ mo, isContinuation: false }];
				}
				const moEnd = mo.endTime ? new Date(mo.endTime) : null;
				if (moEnd && dayStart > moStart && dayStart < moEnd) {
					return [{ mo, isContinuation: true }];
				}
				return [];
			})
			.sort((a, b) => new Date(a.mo.startTime).getTime() - new Date(b.mo.startTime).getTime());
	};

	// Get all MOs for a workcenter (across all days)
	const getMOsForWorkcenter = (workcenter: string) => {
		return productionPlan.filter(mo => mo.workstation === workcenter);
	};

	const isBusy = loading || loadingWorkcenters || loadingProducts;
	const hasWorkcenters = !loadingWorkcenters && workcenters.length > 0;
	const shiftLegend = useMemo(() => shiftKey(lineShifts), [lineShifts]);

	return (
		<LineShiftsContext.Provider value={lineShiftsByLine}>
		<DndProvider backend={HTML5Backend}>
		<PlannerPage>
			<PlannerHeader
				title="Work plan"
				subtitle={
					`${isGridMode ? dateRangeText : format(selectedDayDate, "EEEE, MMMM d, yyyy")}${shiftLegend ? ` · Shifts ${shiftLegend}` : ""}`
				}
				actions={
					<>
						{isGridMode && (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.25,
									border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
									borderRadius: `${planner.radiusSm}px`,
									bgcolor: planner.surface,
									px: 0.5,
									py: 0.25,
								}}
							>
								<Button
									type="button"
									size="small"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleToday();
									}}
									disabled={loading}
									sx={{
										...planner.type.bodyStrong,
										textTransform: "none",
										color: planner.ink,
										minWidth: 0,
										px: 1,
									}}
								>
									Today
								</Button>
								<IconButton
									type="button"
									size="small"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handlePreviousWeek();
									}}
									disabled={loading}
									sx={{ color: planner.inkSecondary }}
								>
									<ChevronLeftIcon fontSize="small" />
								</IconButton>
								<IconButton
									type="button"
									size="small"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleNextWeek();
									}}
									disabled={loading}
									sx={{ color: planner.inkSecondary }}
								>
									<ChevronRightIcon fontSize="small" />
								</IconButton>
							</Box>
						)}
						<ToggleButtonGroup
							value={viewMode}
							exclusive
							onChange={(e, newMode: ViewMode | null) => {
								if (newMode !== null) {
									setViewMode(newMode);
									if (newMode === "day") {
										// When switching to day view, set to today if not already set
										if (!selectedDayDate) {
											setSelectedDayDate(new Date());
										}
									}
								}
							}}
							size="small"
							sx={{
								bgcolor: planner.surface,
								"& .MuiToggleButton-root": {
									...planner.type.bodyStrong,
									textTransform: "none",
									px: 1.5,
									py: 0.5,
									color: planner.inkSecondary,
									borderColor: planner.hairlineStrong,
									"&.Mui-selected": {
										bgcolor: planner.accentSoft,
										color: planner.accent,
										"&:hover": { bgcolor: planner.accentSoft },
									},
								},
							}}
						>
							<ToggleButton value="day">
								<ViewDayIcon sx={{ mr: 0.5, fontSize: 16 }} />
								Day
							</ToggleButton>
							<ToggleButton value="week">
								<ViewWeekIcon sx={{ mr: 0.5, fontSize: 16 }} />
								Week
							</ToggleButton>
							<ToggleButton value="2w">2W</ToggleButton>
							<ToggleButton value="4w">4W</ToggleButton>
						</ToggleButtonGroup>
						<GhostButton
							size="small"
							startIcon={<LayersOutlinedIcon sx={{ fontSize: 16 }} />}
							onClick={() => setShowCampaignDialog(true)}
							disabled={loading}
						>
							Campaigns
						</GhostButton>
						<GhostButton
							size="small"
							startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
							onClick={handleDownloadPlan}
							disabled={loading || productionPlan.length === 0}
							title="Download the job orders shown as a spreadsheet"
						>
							Download
						</GhostButton>
						<GhostButton
							size="small"
							startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
							endIcon={<KeyboardArrowDownIcon />}
							onClick={(e) => setUploadMenuAnchor(e.currentTarget)}
							disabled={loading}
						>
							Upload
						</GhostButton>
						<Menu
							anchorEl={uploadMenuAnchor}
							open={Boolean(uploadMenuAnchor)}
							onClose={() => setUploadMenuAnchor(null)}
							anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
							transformOrigin={{ vertical: "top", horizontal: "right" }}
						>
							<MenuItem onClick={() => {
								setUploadMenuAnchor(null);
								setShowUploadDialog(true);
							}}>
								<ListItemIcon>
									<UploadFileIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText>Upload from file</ListItemText>
							</MenuItem>
							<MenuItem onClick={() => {
								setUploadMenuAnchor(null);
								setShowAIUploadDialog(true);
							}}>
								<ListItemIcon>
									<FactCheckOutlinedIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText>Upload and review</ListItemText>
							</MenuItem>
							<MenuItem onClick={() => {
								setUploadMenuAnchor(null);
								setShowJobOrderIngestDialog(true);
							}}>
								<ListItemIcon>
									<DescriptionOutlinedIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText>Import job order</ListItemText>
							</MenuItem>
							<MenuItem onClick={() => {
								setUploadMenuAnchor(null);
								setShowCopyWeekDialog(true);
							}}>
								<ListItemIcon>
									<ContentCopyIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText>Copy Previous Week</ListItemText>
							</MenuItem>
						</Menu>
						{isGridMode && (
							<PrimaryButton
								size="small"
								startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
								onClick={handleSaveProductionPlan}
								disabled={saving || loading}
							>
								{saving ? "Saving…" : "Save plan"}
							</PrimaryButton>
						)}
					</>
				}
			/>

			{isBusy && (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						gap: 1.5,
						py: 4,
						color: planner.inkSecondary,
					}}
				>
					<CircularProgress size={20} sx={{ color: planner.accent }} />
					<Typography sx={{ ...planner.type.body }}>
						{loadingWorkcenters ? "Loading workcenters…" : loadingProducts ? "Loading products…" : "Loading job orders…"}
					</Typography>
				</Box>
			)}

			{!loadingWorkcenters && workcenters.length === 0 && (
				<Box
					sx={{
						bgcolor: planner.surface,
						border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
						borderRadius: `${planner.radius}px`,
						boxShadow: planner.shadowRest,
					}}
				>
					<EmptyState
						title="No work centers configured"
						body={`Configure work centers in the system to start planning.${filterStatus === "error" ? ` (Filter load failed: ${filterError || "Unknown error"})` : ""}`}
					/>
				</Box>
			)}

			{isGridMode && hasWorkcenters && !loading && productionPlan.length === 0 && (
				<Box
					sx={{
						bgcolor: planner.surface,
						border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
						borderRadius: `${planner.radius}px`,
						boxShadow: planner.shadowRest,
						mb: 2,
					}}
				>
					<EmptyState
						compact
						title={`No job orders ${isMultiWeek ? "in this view" : "this week"}`}
						body="Add an order to a work center below, upload a plan, or copy the previous week."
						actionLabel="Add job order"
						onAction={() => handleAdd(workcenters[0], weekDates[0]?.dateString || weekStartDate)}
					/>
				</Box>
			)}

			{isGridMode && hasWorkcenters && (
				<ProductShelf products={products} standards={laborStandards} workcenters={workcenters} orders={productionPlan} />
			)}
			{isGridMode && weekDates.length > 0 && hasWorkcenters && (
				<Box
					ref={setBoardEl}
					sx={{
						bgcolor: planner.surface,
						border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
						borderRadius: `${planner.radius}px`,
						boxShadow: planner.shadowRest,
						// 2W/4W keep day columns at a readable minimum and scroll
						// horizontally inside the board card instead of squeezing.
						// minWidth 0 is what confines that scroll to the card —
						// without it the grid's minWidth sizes the card (and the
						// page header above it) to the full grid width.
						overflowX: isMultiWeek ? "auto" : "visible",
						minWidth: 0,
					}}
				>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: `${WC_COLUMN_WIDTH}px repeat(${spanDays}, minmax(${isMultiWeek ? `${MIN_MULTIWEEK_COLUMN_WIDTH}px` : 0}, 1fr))`,
							minWidth: isMultiWeek
								? WC_COLUMN_WIDTH + spanDays * MIN_MULTIWEEK_COLUMN_WIDTH
								: undefined,
						}}
					>
						{/* Sticky header row: corner cell + one cell per day */}
						<Box
							sx={{
								position: "sticky",
								top: 0,
								left: 0,
								zIndex: 4,
								bgcolor: planner.surface,
								borderBottom: `1px solid ${planner.hairline}`,
								borderRight: isMultiWeek ? `1px solid ${planner.hairline}` : "none",
								borderTopLeftRadius: `${planner.radius}px`,
								px: 1.75,
								py: 1.25,
								display: "flex",
								alignItems: "center",
							}}
						>
							<SectionLabel>Work center</SectionLabel>
						</Box>
						{weekDates.map(({ day, date, dateString }, dayIndex) => {
							const isToday = isSameDay(date, new Date());
							return (
								<Box
									key={`header-${dateString}`}
									sx={{
										position: "sticky",
										top: 0,
										zIndex: 3,
										bgcolor: isToday ? TODAY_HEADER_BG : planner.surface,
										borderBottom: `1px solid ${planner.hairline}`,
										borderLeft: `1px solid ${planner.hairline}`,
										borderTopRightRadius: dayIndex === weekDates.length - 1 ? `${planner.radius}px` : 0,
										px: 1,
										py: 1.25,
										textAlign: "center",
									}}
								>
									<SectionLabel sx={{ color: isToday ? planner.accent : planner.inkMuted }}>
										{day}
									</SectionLabel>
									<Typography
										sx={{
											...planner.type.bodyStrong,
											color: isToday ? planner.accent : planner.inkSecondary,
											fontVariantNumeric: "tabular-nums",
											lineHeight: 1.4,
										}}
									>
										{format(date, "MMM d")}
									</Typography>
								</Box>
							);
						})}

						{/* One row per work center, one cell per day */}
						{workcenters.map((workcenter: string, rowIndex: number) => {
							const weekCount = getMOsForWorkcenter(workcenter).length;
							const isLastRow = rowIndex === workcenters.length - 1;
							return (
								<React.Fragment key={workcenter}>
									<Box
										sx={{
											bgcolor: planner.surfaceSubtle,
											borderTop: rowIndex > 0 ? `1px solid ${planner.hairline}` : "none",
											borderRight: isMultiWeek ? `1px solid ${planner.hairline}` : "none",
											borderBottomLeftRadius: isLastRow ? `${planner.radius}px` : 0,
											px: 1.75,
											py: 1.5,
											// Keep the work-center label visible while the 2W/4W
											// board scrolls horizontally.
											...(isMultiWeek ? { position: "sticky", left: 0, zIndex: 2 } : {}),
										}}
									>
										<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink, lineHeight: 1.3 }}>
											{workcenter}
										</Typography>
										<Typography
											sx={{
												...planner.type.meta,
												color: planner.inkMuted,
												mt: 0.25,
												fontVariantNumeric: "tabular-nums",
											}}
										>
											{weekCount === 0
												? `No orders ${isMultiWeek ? "in view" : "this week"}`
												: `${weekCount} order${weekCount !== 1 ? "s" : ""} ${isMultiWeek ? "in view" : "this week"}`}
										</Typography>
									</Box>
									{weekDates.map(({ date, dateString }, dayIndex) => {
										const entries = getMOsForWorkcenterAndDate(workcenter, date);
										const isToday = isSameDay(date, new Date());
										const isEmpty = entries.length === 0;
										return (
											<DropCell
												key={`${workcenter}-${dateString}`}
												workstation={workcenter}
												day={date}
												onDropProduct={edits.dropProduct}
												onDropOrder={edits.moveOrder}
												sx={{
													borderTop: rowIndex > 0 ? `1px solid ${planner.hairline}` : "none",
													borderLeft: `1px solid ${planner.hairline}`,
													borderBottomRightRadius:
														isLastRow && dayIndex === weekDates.length - 1 ? `${planner.radius}px` : 0,
													bgcolor: isToday ? TODAY_TINT : "transparent",
													p: denseCards ? 0.5 : 1,
													minHeight: 96,
													minWidth: 0,
													display: "flex",
													flexDirection: "column",
													"&:hover .cell-add-btn": { opacity: 1 },
												}}
											>
												{entries.map(({ mo, isContinuation }) => {
													const card = (
														<MOCard
															key={isContinuation ? `${mo.id}-cont-${dateString}` : mo.id}
															mo={mo}
															attributeDefinitions={attributeDefinitions}
															onEdit={() => handleEdit(mo)}
															onCopy={() => handleCopy(mo)}
															onDelete={() => edits.removeOrder(mo)}
															onViewOperations={() => handleViewOperations(mo)}
															canViewOperations={hasOperations(mo)}
															onSwap={(anchor) => openSwap(mo, anchor)}
															continuation={isContinuation}
															dense={denseCards}
														/>
													);
													return isContinuation ? card : (
														<DraggableCard key={mo.id} order={mo}>
															{card}
														</DraggableCard>
													);
												})}
												<Button
													className="cell-add-btn"
													size="small"
													fullWidth
													onClick={(event) => openAddPicker(workcenter, date, event.currentTarget)}
													sx={{
														...planner.type.metaStrong,
														textTransform: "none",
														color: planner.inkMuted,
														border: `${planner.borderWidth}px dashed ${planner.hairlineStrong}`,
														borderRadius: `${planner.radiusSm}px`,
														py: 0.5,
														mt: "auto",
														minWidth: 0,
														whiteSpace: "nowrap",
														opacity: isEmpty ? 1 : 0,
														transition: "opacity 120ms ease, border-color 120ms ease, color 120ms ease",
														"&:hover": {
															borderColor: planner.accent,
															color: planner.accent,
															bgcolor: planner.accentSoft,
														},
													}}
												>
													{denseCards ? "+" : "+ Add job order"}
												</Button>
											</DropCell>
										);
									})}
								</React.Fragment>
							);
						})}
					</Box>
				</Box>
			)}

			<MOForm
				open={showForm}
				editingMO={editingMO}
				onSave={() => {
					setShowForm(false);
					setEditingMO(null);
					setSelectedWorkcenter(null);
					setSelectedDate(null);
					loadManufacturingOrders();
				}}
				onClose={() => {
					setShowForm(false);
					setEditingMO(null);
					setSelectedWorkcenter(null);
					setSelectedDate(null);
				}}
				preselectedWorkcenter={selectedWorkcenter}
				preselectedDate={selectedDate}
				workcenters={workcenters}
				products={products}
			/>

			{showCopyForm && copyingMO && (
				<CopyMOForm
					mo={copyingMO}
					onSave={handleSaveCopiedMOs}
					onCancel={() => {
						setShowCopyForm(false);
						setCopyingMO(null);
					}}
					weekStartDate={weekStartDate}
					workcenters={workcenters}
				/>
			)}

			{viewMode === "day" && (
				<ProductionPlanDayView
					manufacturingOrders={productionPlan}
					selectedDate={selectedDayDate}
					onDateChange={setSelectedDayDate}
					workcenters={workcenters}
					onEditMO={(mo) => {
						setEditingMO(mo);
						setShowForm(true);
					}}
					onDeleteMO={handleDelete}
					onViewOperations={handleViewOperations}
					hasOperations={hasOperations}
				/>
			)}

			{previewStandard && (
				<LaborStandardPreviewDialog
					open
					onClose={() => setPreviewStandard(null)}
					standard={previewStandard}
				/>
			)}

			<UploadProductionPlanDialog
				open={showUploadDialog}
				weekStartDate={parseDateString(weekStartDate)}
				products={products}
				workcenters={workcenters}
				plantLines={plantLines}
				locationId={selectedLocationId}
				prepareOrders={prepareUploadedOrders}
				onClose={() => setShowUploadDialog(false)}
				onUploadSuccess={() => {
					setSnackbar({
						open: true,
						message: "Production plan uploaded successfully",
						severity: "success",
					});
					loadManufacturingOrders();
				}}
			/>

			<AIUploadDialog
				open={showAIUploadDialog}
				onClose={() => setShowAIUploadDialog(false)}
				onSuccess={async (orders) => {
					try {
						const createdMOs = await productionPlanApi.createBatch(prepareUploadedOrders(orders));
						setProductionPlan((prev) => [...prev, ...createdMOs]);
						setSnackbar({
							open: true,
							message: `Successfully created ${createdMOs.length} job orders`,
							severity: "success",
						});
					} catch (error: any) {
						console.error("Error creating MOs from AI upload:", error);
						const errorMessage = error?.response?.data?.message || error?.message || "Failed to create job orders";

						// Check if it's a batch conflict error and provide details
						const batchConflictMatch = errorMessage.match(/Orders (\d+) and (\d+) conflict on workcenter '([^']+)'/i);
						if (batchConflictMatch) {
							const order1Idx = parseInt(batchConflictMatch[1], 10) - 1;
							const order2Idx = parseInt(batchConflictMatch[2], 10) - 1;
							const workcenter = batchConflictMatch[3];
							const order1 = orders[order1Idx];
							const order2 = orders[order2Idx];

							let detailedError = `Conflict on ${workcenter}:\n`;
							if (order1) detailedError += `• ${order1.sku}: ${order1.startTime?.substring(0, 16)}\n`;
							if (order2) detailedError += `• ${order2.sku}: ${order2.startTime?.substring(0, 16)}`;

							setSnackbar({
								open: true,
								message: detailedError,
								severity: "error",
							});
						} else {
							setSnackbar({
								open: true,
								message: errorMessage,
								severity: "error",
							});
						}
					}
				}}
				weekStartDate={weekStartDate}
				products={products.map((p) => ({ id: p.id, sku: p.itemNumber || "", name: p.name }))}
				workcenters={workcenters}
			/>

			<JobOrderIngestDialog
				open={showJobOrderIngestDialog}
				onClose={() => setShowJobOrderIngestDialog(false)}
				onSuccess={loadManufacturingOrders}
				workcenters={workcenters}
			/>

			<CampaignDialog
				open={showCampaignDialog}
				onClose={() => setShowCampaignDialog(false)}
				companyId={companyId}
				products={products}
				workcenters={workcenters}
				onGenerated={loadManufacturingOrders}
				locationId={selectedLocationId ? Number(selectedLocationId) : null}
			/>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity || "info"}
					sx={{ width: "100%" }}
					action={
						snackbar.undo ? (
							<Button
								color="inherit"
								size="small"
								onClick={() => {
									const undo = snackbar.undo;
									setSnackbar({ ...snackbar, open: false, undo: undefined });
									undo?.();
								}}
							>
								Undo
							</Button>
						) : undefined
					}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>

			<ProductPickerPopover
				anchorEl={pickerAnchor}
				target={pickerTarget}
				products={products}
				standards={laborStandards}
				orders={productionPlan}
				onClose={closePicker}
				onAdd={edits.dropProduct}
				onSwap={edits.swapProduct}
				onFullForm={(workstation, day) => handleAdd(workstation, format(day, "yyyy-MM-dd"))}
			/>

			{/* A drop or move onto a taken slot asks before replacing or swapping. */}
			<Dialog open={Boolean(edits.ask)} onClose={edits.dismissAsk} maxWidth="xs" fullWidth>
				<DialogTitle sx={{ fontFamily: planner.fontBrand, color: planner.navy }}>{edits.ask?.title}</DialogTitle>
				<DialogContent sx={{ ...planner.type.body, color: planner.ink }}>{edits.ask?.message}</DialogContent>
				<DialogActions>
					<Button onClick={edits.dismissAsk} sx={{ color: planner.inkSecondary }}>
						Cancel
					</Button>
					<Button onClick={edits.confirmAsk} variant="contained" sx={{ bgcolor: planner.accent, "&:hover": { bgcolor: planner.accentDeep } }}>
						{edits.ask?.confirmLabel}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Copy from Previous Week Preview Dialog */}
			<CopyWeekPreviewDialog
				open={showCopyWeekDialog}
				onClose={() => setShowCopyWeekDialog(false)}
				onSuccess={(message) => {
					setSnackbar({
						open: true,
						message,
						severity: "success",
					});
				}}
				onError={(message) => {
					setSnackbar({
						open: true,
						message,
						severity: "error",
					});
				}}
				weekStartDate={weekStartDate}
				currentWeekOrders={productionPlan}
				onOrdersUpdated={loadManufacturingOrders}
			/>
		</PlannerPage>
		</DndProvider>
		</LineShiftsContext.Provider>
	);
};

export default ProductionPlan;
